import { type FormEvent, useCallback, useEffect, useState } from "react";

import {
  api,
  type CrmServiceRequest,
  type CrmServiceRequestSource,
  type CrmServiceRequestStatus,
  type CrmServiceRequestType,
} from "@/lib/api";

type SourceFilter = "" | CrmServiceRequestSource;
type StatusFilter = "" | CrmServiceRequestStatus;
type TypeFilter = "" | CrmServiceRequestType;

interface RequestFilters {
  q: string;
  source: SourceFilter;
  status: StatusFilter;
  request_type: TypeFilter;
}

const EMPTY_FILTERS: RequestFilters = {
  q: "",
  source: "",
  status: "",
  request_type: "",
};

const STATUS_LABELS: Record<string, string> = {
  new: "Новая",
  processing: "В работе",
  closed: "Закрыта",
};

const STATUS_STYLES: Record<string, string> = {
  new: "border-info-border bg-info-bg text-info",
  processing: "border-warn-border bg-warn-bg text-warn",
  closed: "border-success-border bg-success-bg text-success",
};

const SOURCE_LABELS: Record<string, string> = {
  web: "Сайт",
  telegram: "Telegram",
};

const TYPE_LABELS: Record<string, string> = {
  service: "Услуга",
  consultation: "Консультация",
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function requestPath(request: CrmServiceRequest) {
  const path = request.payload_json?.path;
  return typeof path === "string" && path.trim() ? path : null;
}

function requestEntryPointLabel(request: CrmServiceRequest) {
  const label = request.payload_json?.entry_point_label;
  return typeof label === "string" && label.trim() ? label : null;
}

function isWidgetRequest(request: CrmServiceRequest) {
  return request.payload_json?.entry_point === "support_widget";
}

function isHomeSelectionRequest(request: CrmServiceRequest) {
  return request.payload_json?.entry_point === "home_selection_form";
}

function normalizeFilters(filters: RequestFilters) {
  return {
    q: filters.q.trim() || undefined,
    source: filters.source || undefined,
    status: filters.status || undefined,
    request_type: filters.request_type || undefined,
  };
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`status-badge ${STATUS_STYLES[status] || "border-border bg-panel-2 text-ink-3"}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function RequestsPage() {
  const [requests, setRequests] = useState<CrmServiceRequest[]>([]);
  const [filters, setFilters] = useState<RequestFilters>(EMPTY_FILTERS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<CrmServiceRequest | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [nextStatus, setNextStatus] = useState<CrmServiceRequestStatus>("new");
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState("");

  const loadRequests = useCallback((nextFilters: RequestFilters) => {
    setLoading(true);
    setError("");
    return api
      .serviceRequests(normalizeFilters(nextFilters))
      .then((result) => {
        setRequests(result);
        setSelected((current) => (current ? result.find((item) => item.id === current.id) ?? current : current));
      })
      .catch((caught) => {
        setError(caught instanceof Error ? caught.message : "Не удалось загрузить заявки");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    void loadRequests(EMPTY_FILTERS);
  }, [loadRequests]);

  const applyFilters = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void loadRequests(filters);
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    void loadRequests(EMPTY_FILTERS);
  };

  const openRequest = (request: CrmServiceRequest) => {
    setSelectedId(request.id);
    setSelected(request);
    setNextStatus(request.status as CrmServiceRequestStatus);
    setDetailError("");
    setStatusError("");
    setDetailLoading(true);
    api
      .serviceRequest(request.id)
      .then((detail) => {
        setSelected(detail);
        setNextStatus(detail.status as CrmServiceRequestStatus);
      })
      .catch((caught) => setDetailError(caught instanceof Error ? caught.message : "Не удалось загрузить заявку"))
      .finally(() => setDetailLoading(false));
  };

  const closeDetail = () => {
    setSelectedId(null);
    setSelected(null);
    setDetailError("");
    setStatusError("");
  };

  const updateStatus = async () => {
    if (!selectedId || !selected || nextStatus === selected.status || statusSaving) return;
    setStatusSaving(true);
    setStatusError("");
    try {
      const updated = await api.updateServiceRequestStatus(selectedId, { status: nextStatus });
      setSelected(updated);
      setRequests((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    } catch (caught) {
      setStatusError(caught instanceof Error ? caught.message : "Не удалось обновить статус заявки");
    } finally {
      setStatusSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm text-ink-4">Обращения с сайта и Telegram о консультациях и услугах.</p>
          <p className="mt-1 text-xs text-ink-mute">В записи сохраняются источник и страница первоначального интереса.</p>
        </div>
        <button type="button" onClick={() => void loadRequests(filters)} className="btn-secondary">
          <i className="ri-refresh-line" />
          Обновить
        </button>
      </div>

      <form onSubmit={applyFilters} className="panel grid gap-3 p-4 lg:grid-cols-[1.5fr_repeat(3,minmax(140px,1fr))_auto]">
        <input
          value={filters.q}
          onChange={(event) => setFilters((current) => ({ ...current, q: event.target.value }))}
          placeholder="ФИО, телефон или email"
          className="soft-input"
        />
        <select
          value={filters.status}
          onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value as StatusFilter }))}
          className="soft-select"
        >
          <option value="">Все статусы</option>
          <option value="new">Новые</option>
          <option value="processing">В работе</option>
          <option value="closed">Закрытые</option>
        </select>
        <select
          value={filters.source}
          onChange={(event) => setFilters((current) => ({ ...current, source: event.target.value as SourceFilter }))}
          className="soft-select"
        >
          <option value="">Все источники</option>
          <option value="web">Сайт</option>
          <option value="telegram">Telegram</option>
        </select>
        <select
          value={filters.request_type}
          onChange={(event) => setFilters((current) => ({ ...current, request_type: event.target.value as TypeFilter }))}
          className="soft-select"
        >
          <option value="">Все типы</option>
          <option value="consultation">Консультации</option>
          <option value="service">Услуги</option>
        </select>
        <div className="flex gap-2">
          <button type="submit" className="btn-primary px-4">
            Найти
          </button>
          <button type="button" onClick={resetFilters} className="btn-secondary px-4">
            Сбросить
          </button>
        </div>
      </form>

      {error ? <div className="rounded-xl border border-danger-border bg-danger-bg px-4 py-3 text-sm text-danger">{error}</div> : null}

      <div className="panel overflow-hidden">
        {loading ? (
          <div className="px-5 py-10 text-center text-sm text-ink-4">Загрузка заявок...</div>
        ) : requests.length === 0 ? (
          <div className="px-5 py-14 text-center">
            <i className="ri-inbox-2-line text-3xl text-ink-mute" />
            <p className="mt-3 text-sm font-medium text-ink-3">Заявок пока нет</p>
            <p className="mt-1 text-xs text-ink-4">Новые обращения появятся здесь после отправки формы.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed text-left text-sm">
              <colgroup>
                <col className="w-[140px]" />
                <col className="w-[72px]" />
                <col className="w-[112px]" />
                <col className="w-[92px]" />
                <col />
                <col className="w-[110px]" />
                <col className="w-[130px]" />
                <col className="w-[150px]" />
              </colgroup>
              <thead className="border-b border-border-soft bg-panel-2 text-xs uppercase tracking-[0.12em] text-ink-mute">
                <tr>
                  <th className="px-2 py-3 font-semibold">Дата</th>
                  <th className="px-2 py-3 font-semibold">Статус</th>
                  <th className="px-2 py-3 font-semibold">Тип</th>
                  <th className="px-2 py-3 font-semibold">Источник</th>
                  <th className="px-2 py-3 font-semibold">Интерес</th>
                  <th className="px-2 py-3 font-semibold">Клиент</th>
                  <th className="px-2 py-3 font-semibold">Телефон</th>
                  <th className="px-2 py-3 font-semibold">Email</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-soft">
                {requests.map((request) => (
                  <tr key={request.id} onClick={() => openRequest(request)} className="cursor-pointer transition hover:bg-panel-2">
                    <td className="truncate whitespace-nowrap px-2 py-4 text-ink-3" title={formatDate(request.created_at)}>{formatDate(request.created_at)}</td>
                    <td className="whitespace-nowrap px-2 py-4"><StatusBadge status={request.status} /></td>
                    <td className="truncate whitespace-nowrap px-2 py-4 text-ink-3">{TYPE_LABELS[request.request_type] || request.request_type}</td>
                    <td className="truncate whitespace-nowrap px-2 py-4 text-ink-3">{SOURCE_LABELS[request.source] || request.source}</td>
                    <td className="px-2 py-4 font-medium text-ink" title={request.page_title}>
                      <div className="flex min-w-0 items-center gap-2">
                        {isWidgetRequest(request) ? (
                          <span className="shrink-0 rounded-full border border-warn-border bg-warn-bg px-2 py-0.5 text-[11px] font-semibold text-warn">
                            Виджет
                          </span>
                        ) : null}
                        {isHomeSelectionRequest(request) ? (
                          <span className="shrink-0 rounded-full border border-info-border bg-info-bg px-2 py-0.5 text-[11px] font-semibold text-info">
                            Главная форма
                          </span>
                        ) : null}
                        <span className="truncate">{request.page_title}</span>
                      </div>
                    </td>
                    <td className="truncate whitespace-nowrap px-2 py-4 text-ink-2" title={request.full_name}>{request.full_name}</td>
                    <td className="truncate whitespace-nowrap px-2 py-4 text-ink-3" title={request.phone}>{request.phone}</td>
                    <td className="truncate whitespace-nowrap px-2 py-4 text-ink-3" title={request.email}>{request.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedId ? (
        <div className="fixed inset-0 z-40 bg-black/20" onClick={closeDetail}>
          <aside
            className="absolute inset-y-0 right-0 flex w-full max-w-xl flex-col border-l border-border bg-panel shadow-ds-3"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-border px-6 py-5">
              <div>
                <p className="section-kicker">Заявка</p>
                <h2 className="mt-1 text-lg font-semibold text-ink">{selected?.page_title || "Загрузка..."}</h2>
              </div>
              <button type="button" onClick={closeDetail} className="btn-secondary px-3" aria-label="Закрыть">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto p-6">
              {detailError ? <div className="rounded-xl border border-danger-border bg-danger-bg px-4 py-3 text-sm text-danger">{detailError}</div> : null}
              {statusError ? <div className="rounded-xl border border-danger-border bg-danger-bg px-4 py-3 text-sm text-danger">{statusError}</div> : null}
              {detailLoading && !selected ? <p className="text-sm text-ink-4">Загрузка...</p> : null}
              {selected ? (
                <>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="rounded-xl bg-panel-2 p-3">
                      <p className="text-xs text-ink-4">Статус</p>
                      <div className="mt-2"><StatusBadge status={selected.status} /></div>
                    </div>
                    <div className="rounded-xl bg-panel-2 p-3">
                      <p className="text-xs text-ink-4">Источник</p>
                      <p className="mt-2 text-sm font-medium text-ink">{SOURCE_LABELS[selected.source] || selected.source}</p>
                    </div>
                    <div className="rounded-xl bg-panel-2 p-3">
                      <p className="text-xs text-ink-4">Тип</p>
                      <p className="mt-2 text-sm font-medium text-ink">{TYPE_LABELS[selected.request_type] || selected.request_type}</p>
                    </div>
                  </div>

                  <div className="panel-soft p-4">
                    <p className="section-kicker">Обработка</p>
                    <div className="mt-3 flex gap-2">
                      <select
                        value={nextStatus}
                        onChange={(event) => setNextStatus(event.target.value as CrmServiceRequestStatus)}
                        className="soft-select flex-1"
                      >
                        <option value="new">Новая</option>
                        <option value="processing">В работе</option>
                        <option value="closed">Закрыта</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => void updateStatus()}
                        disabled={statusSaving || nextStatus === selected.status}
                        className="btn-primary"
                      >
                        {statusSaving ? "Сохраняем..." : "Сохранить"}
                      </button>
                    </div>
                  </div>

                  <div className="panel-soft p-4">
                    <p className="section-kicker">Клиент</p>
                    <p className="mt-3 text-sm font-semibold text-ink">{selected.full_name}</p>
                    <p className="mt-2 text-sm text-ink-3">{selected.phone}</p>
                    <p className="mt-1 text-sm text-ink-3">{selected.email}</p>
                  </div>

                  <div className="panel-soft p-4">
                    <p className="section-kicker">Запрос</p>
                    <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-ink-2">
                      {selected.request_text || "Текст запроса не указан"}
                    </p>
                  </div>

                  <div className="panel-soft p-4">
                    <p className="section-kicker">Откуда пришёл клиент</p>
                    <dl className="mt-3 grid gap-3 text-sm">
                      {requestEntryPointLabel(selected) ? (
                        <div>
                          <dt className="text-ink-4">Канал заявки</dt>
                          <dd className="mt-1 font-medium text-ink">{requestEntryPointLabel(selected)}</dd>
                        </div>
                      ) : null}
                      <div>
                        <dt className="text-ink-4">Первоначальный интерес</dt>
                        <dd className="mt-1 font-medium text-ink">{selected.page_title}</dd>
                      </div>
                      {selected.page_slug ? (
                        <div>
                          <dt className="text-ink-4">Идентификатор страницы</dt>
                          <dd className="mt-1 text-ink-3">{selected.page_slug}</dd>
                        </div>
                      ) : null}
                      {requestPath(selected) ? (
                        <div>
                          <dt className="text-ink-4">Путь отправки</dt>
                          <dd className="mt-1 font-mono text-xs text-ink-3">{requestPath(selected)}</dd>
                        </div>
                      ) : null}
                      <div>
                        <dt className="text-ink-4">Создана</dt>
                        <dd className="mt-1 text-ink-3">{formatDate(selected.created_at)}</dd>
                      </div>
                    </dl>
                  </div>
                </>
              ) : null}
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
}
